import { formatDate, today } from '@seen/core/dates'
import type { ExtendedObservation } from '@seen/core/models/observation'
import { unitLabel } from '@seen/core/models/observationComponents'
import type { JSONContent } from '@tiptap/core'
import { format, isToday, isYesterday, subDays } from 'date-fns'
import { extractBlockLinesFromHTML } from '#src/utils/html.ts'

// What an earlier observation recorded, as one short string with readable
// units: a number with its unit, its parts (blood pressure), its answer, or
// the record it was answered with.
export function shownValue(
  observation: ExtendedObservation
): string | undefined {
  const quantity = observation.valueQuantity
  if (quantity !== undefined && quantity !== null) {
    const unit = observation.valueUnit
    return unit ? `${quantity} ${unitLabel(unit)}` : String(quantity)
  }
  return (
    observation.componentDisplay ||
    observation.valueDisplay ||
    observation.focusReference?.display
  )
}

// When an observation was made: "today", "yesterday", or its date.
export function observationDate(
  observation: ExtendedObservation
): string | null {
  const date =
    observation.effectiveDateTime ??
    observation.issued ??
    observation.toFhirResource().meta?.lastUpdated
  if (!date) {
    return null
  }
  // A bare day names a calendar day, not an instant in UTC.
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    if (date === today()) {
      return 'today'
    }
    if (date === format(subDays(new Date(), 1), 'yyyy-MM-dd')) {
      return 'yesterday'
    }
    return formatDate(date)
  }
  const instant = new Date(date)
  if (isToday(instant)) {
    return 'today'
  }
  if (isYesterday(instant)) {
    return 'yesterday'
  }
  return formatDate(date)
}

export interface LastValue {
  value: string
  date: string | null
}

export function lastValue(
  observation: ExtendedObservation | undefined
): LastValue | undefined {
  if (!observation) {
    return undefined
  }
  const value = shownValue(observation)
  return value ? { value, date: observationDate(observation) } : undefined
}

const HTML_TAG = /<[a-z][^>]*>/i
const FIRST_LINE_LENGTH = 60

// Free text as recorded: HTML from the note editor, or plain text whose line
// breaks are newlines. Its lines, blank ones dropped.
function textLines(text: string): string[] {
  return HTML_TAG.test(text)
    ? extractBlockLinesFromHTML(text)
    : text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
}

// The first line of earlier free text, cut short, for its last-value chip.
export function firstLineOf(text: string): string {
  const [first = ''] = textLines(text)
  return first.length > FIRST_LINE_LENGTH
    ? `${first.slice(0, FIRST_LINE_LENGTH - 1).trimEnd()}…`
    : first
}

// Earlier free text as editor content, one paragraph per line.
export function textContent(text: string): string | JSONContent {
  if (HTML_TAG.test(text)) {
    return text
  }
  return {
    type: 'doc',
    content: text
      .split('\n')
      .map((line) =>
        line
          ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
          : { type: 'paragraph' }
      ),
  }
}
